package one.healthium.ui.compose.components

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldColors
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_14_60
import one.healthium.ui.compose.theme.gray_2
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds

@Composable
fun AppTextField(
    modifier: Modifier = Modifier,
    value: TextFieldValue,
    onValueChange: (TextFieldValue) -> Unit,
    textStyle: TextStyle = LocalTextStyle.current,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    maxLines: Int = Int.MAX_VALUE,
    showKeyboardAutomaticallyIfEmpty: Boolean = false,
    showKeyboardAutomatically: Boolean = false,
    showKeyboardDelay: Duration = 0.seconds,
    colors: TextFieldColors = AppTextFieldDefaults.colors,
    shape: Shape = TextFieldDefaults.shape,
    label: @Composable (() -> Unit)? = null,
    placeholder: @Composable (() -> Unit)? = null,
    visualTransformation: VisualTransformation = VisualTransformation.None,
) {
    val focusRequester = remember { FocusRequester() }

    LaunchedEffect(focusRequester, showKeyboardAutomaticallyIfEmpty, showKeyboardAutomatically, value) {
        if ((showKeyboardAutomaticallyIfEmpty && value.text.isEmpty()) || showKeyboardAutomatically) {
            delay(showKeyboardDelay.inWholeMilliseconds)
            focusRequester.requestFocus()
        }
    }

    TextField(
        modifier = modifier.focusRequester(focusRequester),
        value = value,
        onValueChange = onValueChange,
        textStyle = textStyle,
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        maxLines = maxLines,
        colors = colors,
        shape = shape,
        label = label,
        placeholder = placeholder,
        visualTransformation = visualTransformation,
    )
}


@Composable
fun AppTextField(
    modifier: Modifier = Modifier,
    value: String,
    onValueChange: (String) -> Unit,
    textStyle: TextStyle = LocalTextStyle.current,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    maxLines: Int = Int.MAX_VALUE,
    showKeyboardAutomaticallyIfEmpty: Boolean = false,
    showKeyboardAutomatically: Boolean = false,
    showKeyboardDelay: Duration = 0.seconds,
    colors: TextFieldColors = AppTextFieldDefaults.colors,
    shape: Shape = TextFieldDefaults.shape,
    label: @Composable (() -> Unit)? = null,
    placeholder: @Composable (() -> Unit)? = null,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    leadingIcon: @Composable (() -> Unit)? = null,
    trailingIcon: @Composable (() -> Unit)? = null,
) {
    val focusRequester = remember { FocusRequester() }

    LaunchedEffect(focusRequester, showKeyboardAutomaticallyIfEmpty, showKeyboardAutomatically, value) {
        if ((showKeyboardAutomaticallyIfEmpty && value.isEmpty()) || showKeyboardAutomatically) {
            delay(showKeyboardDelay.inWholeMilliseconds)
            focusRequester.requestFocus()
        }
    }

    TextField(
        modifier = modifier.focusRequester(focusRequester),
        value = value,
        onValueChange = onValueChange,
        textStyle = textStyle,
        colors = colors,
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        maxLines = maxLines,
        shape = shape,
        label = label,
        placeholder = placeholder,
        visualTransformation = visualTransformation,
        leadingIcon = leadingIcon,
        trailingIcon = trailingIcon,
    )
}

object AppTextFieldDefaults {
    val colors
        @Composable get() = TextFieldDefaults.colors(
            focusedContainerColor = Color.Transparent,
            unfocusedContainerColor = Color.Transparent,
            disabledContainerColor = Color.Transparent,
            errorContainerColor = Color.Transparent,
            focusedIndicatorColor = AppTheme.colors.divider,
            unfocusedIndicatorColor = AppTheme.colors.divider,
        )
    val colorsGray
        @Composable get() = TextFieldDefaults.colors(
            focusedContainerColor = gray_2,
            unfocusedContainerColor = gray_2,
            disabledContainerColor = gray_2,
            errorContainerColor = gray_2,
            focusedIndicatorColor = Color.Transparent,
            unfocusedIndicatorColor = Color.Transparent,
            focusedPlaceholderColor = gray_14_60,
            unfocusedPlaceholderColor = gray_14_60,
        )

    val shapeRounded
        @Composable get() = AppTheme.shapes.small
}

@Composable
@Preview
private fun AppTextFieldPreview() {
    AppTheme {
        Column(
            modifier = Modifier.padding(24.dp)
        ) {
            AppTextField(
                modifier = Modifier.fillMaxWidth(),
                value = "",
                onValueChange = {}
            )
        }
    }
}